import * as Tabs from '@radix-ui/react-tabs';
import { forwardRef, ReactNode, Ref, useContext } from 'react';
import styled from 'styled-components';
import { TabNavigationContext } from './_tab_navigation.context';
import { TabNavigationProvider } from './_tab_navigation_provider';

export interface TabNavigationRootProps {
	children: ReactNode | ReactNode[];

	/**
	 * The value of the tab that should be active when initially rendered. Use
	 * when you do not need to control the state of the tabs.
	 */
	defaultValue?: string;

	/**
	 * Event handler called when the active tab is changes.
	 */
	onChange?: (value: string) => void;
}

const elements = {
	tabRoot: styled(Tabs.Root)`
		display: contents;
	`,
};

/* @internal */
export function Root({ children, defaultValue, onChange, ...rest }: TabNavigationRootProps): JSX.Element {
	return (
		<TabNavigationProvider defaultValue={defaultValue} onChange={onChange}>
			<TabNavigationRootWrapper activationMode='manual' {...rest}>
				{children}
			</TabNavigationRootWrapper>
		</TabNavigationProvider>
	);
}

interface TabRootWrapperProps extends Tabs.TabsProps {
	children: ReactNode;
}

// #region internals
/* @internal */
const TabNavigationRootWrapper = forwardRef(function TabNavigationRootWrapper(
	{ children, ...rest }: TabRootWrapperProps,
	ref: Ref<HTMLDivElement>,
): JSX.Element {
	const { activeTab, changeTab } = useContext(TabNavigationContext);

	return (
		<elements.tabRoot value={activeTab} orientation='horizontal' onValueChange={changeTab} ref={ref} {...rest}>
			{children}
		</elements.tabRoot>
	);
});
// #endregion
